import React, { useState, useEffect, useCallback } from "react";
import axios from 'axios';

const NotificationUI = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Memoized notification data fetch
    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const userid = localStorage.getItem("userid");
            if (!userid) {
                setError("User not logged in");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('userid', userid);
            
            const response = await axios.post('https://octafxx.in/Rest/notifs_api/'+userid, formData);
            setNotifications(response.data || []);
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setError("Failed to load notifications");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Get notification color based on type
    const getNotificationColor = (type) => {
        const colors = {
            "trade": "#8346FF",
            "price": "#16FF99", 
            "deposit": "#3B82F6",
            "withdrawal": "#FF4C5B",
            "market": "#F59E0B",
            "system": "#6B7280",
            "alert": "#EF4444",
            "info": "#10B981"
        };
        return colors[type] || "#8346FF";
    };

    // Get notification icon based on type
    const getNotificationIcon = (type) => {
        const icons = {
            "trade": "fa-exchange-alt",
            "price": "fa-chart-line",
            "deposit": "fa-arrow-down",
            "withdrawal": "fa-arrow-up",
            "market": "fa-bullhorn",
            "system": "fa-cog",
            "alert": "fa-exclamation-triangle",
            "info": "fa-info-circle"
        };
        return icons[type] || "fa-bell";
    };

    // Format date for better readability
    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Yesterday';
        if (diffDays > 1) return `${diffDays} days ago`;
        
        // For today, show time
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        // Implement mark as read functionality
        console.log('Mark as read:', notificationId);
    };

    // Clear all notifications
    const clearAllNotifications = () => {
        if (window.confirm('Are you sure you want to clear all notifications?')) {
            setNotifications([]);
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 bg-light">
                <div className="container-fluid py-4">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading notifications...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-vh-100 bg-light">
                <div className="container-fluid py-4">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="alert alert-danger text-center">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 bg-light">
            {/* Header Section */}
          

            {/* Main Content */}
            <div className="container-fluid py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        {notifications.length === 0 ? (
                            /* Empty State */
                            <div className="card border-0 shadow-sm text-center py-5">
                                <div className="card-body">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4" 
                                         style={{width: '80px', height: '80px'}}>
                                        <i className="fas fa-bell fa-2x text-primary"></i>
                                    </div>
                                    <h5 className="text-dark mb-3">No Notifications</h5>
                                    <p className="text-muted mb-4">
                                        You're all caught up! No new notifications at the moment.
                                    </p>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={fetchNotifications}
                                    >
                                        <i className="fas fa-sync-alt me-2"></i>
                                        Refresh
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* Notifications List */
                            <div className="notifications-list">
                                {notifications.map((notification, index) => (
                                    <div 
                                        key={notification.id || index}
                                        className={`card border-0 shadow-sm mb-3 notification-item ${
                                            !notification.read ? 'unread' : ''
                                        }`}
                                        onClick={() => markAsRead(notification.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="card-body">
                                            <div className="row align-items-start">
                                                {/* Notification Icon */}
                                                <div className="col-auto">
                                                    <div 
                                                        className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            backgroundColor: getNotificationColor(notification.type),
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <i className={`fas ${getNotificationIcon(notification.type)}`}></i>
                                                    </div>
                                                </div>
                                                
                                                {/* Notification Content */}
                                                <div className="col">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="mb-0 fw-bold text-dark">
                                                            {notification.ntitle}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {formatDate(notification.ndatetime)}
                                                        </small>
                                                    </div>
                                                    <p className="text-muted mb-0">
                                                        {notification.ndesc}
                                                    </p>
                                                    
                                                    {/* Notification Type Badge */}
                                                    <div className="mt-2">
                                                        <span 
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: `${getNotificationColor(notification.type)}20`,
                                                                color: getNotificationColor(notification.type),
                                                                fontSize: '0.7rem'
                                                            }}
                                                        >
                                                            {notification.type?.toUpperCase() || 'NOTIFICATION'}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {/* Unread Indicator */}
                                                {!notification.read && (
                                                    <div className="col-auto">
                                                        <div 
                                                            className="rounded-circle bg-primary"
                                                            style={{
                                                                width: '8px',
                                                                height: '8px'
                                                            }}
                                                        ></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Load More Button (if pagination is implemented) */}
                        {notifications.length > 0 && (
                            <div className="text-center mt-4">
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={fetchNotifications}
                                >
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Load More
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Custom CSS */}
            <style jsx>{`
                .notification-item {
                    transition: all 0.2s ease;
                    border-radius: 12px;
                }
                
                .notification-item:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                }
                
                .notification-item.unread {
                    border-left: 4px solid #8346FF;
                    background: linear-gradient(135deg, #f8faff, #ffffff);
                }
                
                .notifications-list {
                    max-height: 70vh;
                    overflow-y: auto;
                }
                
                .notifications-list::-webkit-scrollbar {
                    width: 6px;
                }
                
                .notifications-list::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .notifications-list::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .notifications-list::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
        </div>
    );
};

export default NotificationUI;